/**
 * Authentication service — login, logout, token storage.
 * Uses the public /auth/login endpoint (no auth header required).
 */

import axios from 'axios';
import { API_BASE_URL } from './api';

export interface AuthUser {
  id: string;
  username: string;
  email?: string;
  is_active: boolean;
  company_id: string;
  company_name: string;
  created_at: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  company_id: string;
  company_name: string;
}

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

/** POST /auth/login — returns AuthUser on success, throws on failure */
export async function login(username: string, password: string): Promise<AuthUser> {
  const res = await axios.post<LoginResponse>(`${API_BASE_URL}/auth/login`, {
    username,
    password,
  });
  const { access_token, company_id, company_name } = res.data;
  localStorage.setItem(TOKEN_KEY, access_token);

  // Fetch full user info with the new token
  const meRes = await axios.get<AuthUser>(`${API_BASE_URL}/auth/me`, {
    headers: { Authorization: `Bearer ${access_token}` },
  });

  // Merge company fields from login response in case /me doesn't return them yet
  const user: AuthUser = {
    ...meRes.data,
    company_id: meRes.data.company_id || company_id,
    company_name: meRes.data.company_name || company_name,
  };
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  return user;
}

export function logout(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
}

export function isAuthenticated(): boolean {
  return !!getToken();
}

// User management (requires auth)
export interface UserCreate {
  username: string;
  password: string;
  email?: string;
}

export interface UserUpdate {
  password?: string;
  email?: string;
  is_active?: boolean;
}
